import { Link } from "react-router-dom";
import { ExternalLink, Linkedin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import { getLocalizedPath } from "@/utils/routes";
import { CaireLovingHandsMark, CaireWordmark } from "@appcaire/ui";
import { getFooterConfig } from "./navConfig";

const EIRTECH_URL = "https://www.eirtech.ai/";

/**
 * Footer component that properly handles both Swedish and English
 */
export const Footer = () => {
  const { language } = useLanguage();
  /** Bind to URL language so footer strings match /en vs Swedish paths before i18n.language catches up */
  const { t: tFooter } = useTranslation("footer", { lng: language });

  // Helper to get localized paths
  const link = (key: string) => getLocalizedPath(key, language);
  const footerConfig = getFooterConfig(tFooter, language);

  const currentYear = new Date().getFullYear();
  const footerLinkClass = "text-[#d7dde6] transition-colors hover:text-white";
  const footerLegalLinkClass =
    "text-[#b5bfca] transition-colors hover:text-white";
  const eirtechFooterLinkClass =
    "inline-flex items-center gap-1 font-semibold text-caire-accent-soft underline decoration-current underline-offset-4 transition-colors hover:text-white";

  return (
    <footer className="border-t border-[#1b3545] bg-[#0d2535] text-sm leading-relaxed text-[#d7dde6]">
      {/* Compact brand/contact row followed by the five shared navigation hubs */}
      <div className="mx-auto w-full max-w-[1440px] px-6 py-10 lg:px-10">
        <div className="space-y-8">
          {/* Company Info & Branding */}
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            {/* Logo & Company Description */}
            <div className="max-w-xl">
              <div className="mb-3 flex items-center">
                <span className="mr-3 flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--cb-action)] text-white">
                  <CaireLovingHandsMark className="h-5 w-5" />
                </span>
                <CaireWordmark
                  className="text-2xl text-white"
                  variant="inverse"
                  showDescriptor
                  locale={language === "en" ? "en" : "sv"}
                />
              </div>
              <p className="max-w-md text-base leading-relaxed text-[#d7dde6]">
                {tFooter(
                  "company_description",
                  language === "sv"
                    ? "CAIRE skapar teknik med hjärta för hemtjänsten."
                    : "CAIRE builds technology with a human touch for home care.",
                )}
              </p>
            </div>

            {/* Social & Contact */}
            <div className="flex shrink-0 items-center gap-4">
              <a
                href="https://www.linkedin.com/company/caire-homecare"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-white transition-colors hover:text-[var(--cb-accent)]"
              >
                <Linkedin className="h-6 w-6" />
              </a>
              <Link
                to={link("contact")}
                className="rounded-lg bg-coral-text px-6 py-2 font-medium text-coral-fg transition-colors hover:bg-coral-action-hover"
              >
                {tFooter("contact_us", "Contact us")}
              </Link>
            </div>
          </div>

          {/* Navigation Links.
              PLATTFORM / LÖSNINGAR / TRYGGHET / RESURSER / FÖRETAG — columns sourced
              from getFooterConfig. Taxonomy mirrors the header for crawl
              clarity; each column is a curated hub list (not a mega-menu
              dump) so commercial and content URLs keep internal-link equity. */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-7 md:grid-cols-3 lg:grid-cols-5">
            {(
              [
                ["product", footerConfig.product],
                ["solutions", footerConfig.solutions],
                ["trust", footerConfig.trust],
                ["resources", footerConfig.resources],
                ["company", footerConfig.company],
              ] as const
            ).map(([id, section]) => (
              <nav key={id} aria-labelledby={`footer-${id}`}>
                <h3
                  id={`footer-${id}`}
                  className="mb-3 text-sm font-semibold text-white"
                >
                  {section.label}
                </h3>
                <ul className="space-y-2">
                  {section.items.map((item) => (
                    <li key={item.href}>
                      {item.target === "_blank" ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel={item.rel ?? "noreferrer"}
                          className={footerLinkClass}
                        >
                          {item.label}
                        </a>
                      ) : (
                        <Link to={item.href} className={footerLinkClass}>
                          {item.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar - Legal & Copyright */}
      <div className="border-t border-white/10">
        <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center justify-between gap-4 px-6 py-5 md:flex-row lg:px-10">
          <p className="text-sm text-[#b5bfca]">
            © {currentYear}{" "}
            <a
              href={EIRTECH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={eirtechFooterLinkClass}
              aria-label="EirTech AB"
            >
              EirTech AB
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
            . {tFooter("rights", "All rights reserved.")}
            <span className="ml-2 whitespace-nowrap">Org-nr 559522-3800</span>
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
            <Link to={link("legal.privacy")} className={footerLegalLinkClass}>
              {tFooter("privacy_policy", "Privacy")}
            </Link>
            <Link to={link("legal.terms")} className={footerLegalLinkClass}>
              {tFooter("terms", "Terms")}
            </Link>
            <button id="cookie-btn" className={footerLegalLinkClass}>
              {tFooter("cookies", "Cookies")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
